/* global AppSettings */
/**
 * This is an example for a component that should only have one instance (Singleton).
 *
 * It's only meant for Singleton components that are initialized by an external component.
 *
 * For more guidelines see `ExampleComponent.js`
 *
 * @author  Meinaart van Straalen
 **/

import $ from 'jquery';
import Singleton from 'components/utils/Singleton';
import createLogger from 'components/logger/Logger';
import device from 'components/utils/device';
import AnalyticsEventTypes from 'components/analytics/AnalyticsEventTypes';
import EventTypes from 'components/EventTypes';
import matchBreakpoint from 'components/utils/matchBreakpoint';
import StoreFinderComponent from './StoreFinderComponent';
import 'jquery.resizeEvents';
import 'jquery.extendPrototype';

const Logger = createLogger('Filters');

function StoreFinderFilters(settings) {
  Logger.info('Created');
  StoreFinderComponent.call(this, settings, module);

  this.$backButton = this.findElement(this.settings.backButtonSelector);
  this.$filters = this.findElement(this.settings.filterSelector);
  this.$list = this.findElement(this.settings.listSelector);
  this.$resetButton = this.findElement(this.settings.resetButtonSelector);
  this.$wrapper = this.$element.parents(this.settings.wrapperSelector);

  this.baseUrl = AppSettings.endpoints.storeFinder;
  this.bindEvents();
}

$.extendPrototype(StoreFinderFilters, StoreFinderComponent.prototype, {
  $backButton: undefined,
  $filters: undefined,
  $list: undefined,
  $resetButton: undefined,
  $wrapper: undefined,
  baseUrl: '',
  settings: {
    backButtonSelector: '.js-storeFinder-filter-backButton',
    filterSelector: '.js-storeFinder-filter-item',
    listSelector: '.js-storeFinder-filter-list',
    resetButtonSelector: '.js-storeFinder-filter-resetButton',
    selector: '.js-storeFinder-filter',
    wrapperSelector: '.js-contentBlock-body',
  },
  filters: undefined,
  isVisible: false,

  /**
   * Update `this.filters` with correct values (selected DOM elements) and dispatch update event
   */
  applyFilters() {
    this.filters = undefined;

    this.$filters.filter('.is-active').each(
      function (index, element) {
        const $filter = $(element);
        const filterData = $filter.data();
        const { filterType } = filterData;

        if (!this.filters) {
          this.filters = {};
        }
        if (!this.filters[filterType]) {
          this.filters[filterType] = [];
        }
        this.filters[filterType].push(filterData.filterId);
      }.bind(this)
    );

    this.$element.trigger(EventTypes.STOREFINDER_STORES_FILTER_REQUEST, {
      filters: this.filters,
    });
  },

  /**
   * Bind events
   */
  bindEvents() {
    document.jq
      .one(EventTypes.STOREFINDER_GET_STORES, this.onInitialize.bind(this))
      .on(EventTypes.STOREFINDER_GET_STORES, this.enableFilters.bind(this))
      .on(EventTypes.STOREFINDER_HIDE_FILTERS_REQUEST, this.toggleVisibility.bind(this, false))
      .on(EventTypes.STOREFINDER_SHOW_FILTERS_REQUEST, this.toggleVisibility.bind(this, true));

    const onClick = event => this.onClick(event);

    this.$backButton
      .on('click', onClick)
      .on('vclick', this.toggleVisibility.bind(this, false, true));
    this.$filters.on('vclick', this.onFilterClick.bind(this));
    this.$resetButton.on('vclick', this.resetFilters.bind(this));

    if (device.supportsPushState) {
      this.updateLinks(window.location.href);
      window.addEventListener('popstate', this.onPopState.bind(this));
    }
  },

  onClick(event) {
    const searchInput = document.querySelector('.js-storeFinder-search-input');

    if (!searchInput) {
      return;
    }

    const filterList = document.querySelector(this.settings.listSelector);
    const filterItems = Array.from(filterList.querySelectorAll(this.settings.filterSelector));
    const activeFilterItems = filterItems.filter(filterItem =>
      filterItem.classList.contains('is-active')
    );
    const activeFilters = activeFilterItems
      .map(activeFilterItem => activeFilterItem.dataset.filterTextEn)
      .join(' | ');

    document.jq.trigger(AnalyticsEventTypes.STORE_FINDER_SEARCH, {
      label: activeFilterItems.length ? activeFilters.toLowerCase() : undefined,
      searchTerm: searchInput.value.toLowerCase(),
    });
  },

  /**
   * On first load of data apply filters if there are any active
   */
  onInitialize() {
    if (this.$filters.filter('.is-active').length) {
      this.applyFilters();
    }
  },

  /**
   * Prevent filters that have no results to be shown
   * @param  {object} event     jQuery event object
   * @param  {object} eventData Data belonging to event
   */
  enableFilters(event, eventData) {
    const { filterResults } = eventData;

    if (typeof filterResults === 'undefined') {
      return;
    }

    const filterButtonAll = document.querySelectorAll(this.settings.filterSelector);
    const filterButton = filterName =>
      document.querySelector(`${this.settings.filterSelector}[data-filter-label="${filterName}"]`);

    filterButtonAll.length && filterButtonAll.forEach(a => (a.style.display = 'none'));

    !!filterResults &&
      filterResults
        .filter(a => a.count > 0)
        .map(a => a.storeType.toLowerCase().split(' ')[0])
        .forEach(a => !!filterButton(a) && (filterButton(a).style.display = 'block'));
  },

  /**
   * Event listener for when a filter is clicked
   * @param  {object} eventData Data object containing `type` and `data`
   * @param  {object} event     jQuery event object
   */
  onFilterClick(event) {
    event.preventDefault();
    event.stopPropagation();

    $(event.target).toggleClass('is-active');
    this.updateLinks(event.target.href);
    this.applyFilters();
  },

  /**
   * Event listener for `popstate` event (when user goes back to previous state)
   * @param  {Event} event
   */
  onPopState(event) {
    const { state } = event;
    if (state && state.activeFilters) {
      this.$filters.each(function (index, element) {
        $(element).toggleClass(
          'is-active',
          state.activeFilters.indexOf(element.getAttribute('data-urlfriendly-label')) !== -1
        );
      });

      this.applyFilters();
    }
  },

  /**
   * Deselect all filters and apply them.
   *
   * @param {object} event jQuery event object (optional)
   */
  resetFilters(event) {
    this.$filters.removeClass('is-active');
    this.applyFilters();

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
  },

  /**
   * Toggle visibility of all filters and dispatch an event to update state.
   * @param  {Boolean} isVisible Should filters be visible
   * @param  {Boolean} stopEvent If true and `event` is supplied the event's propagation is stopped
   * @param  {object}  event     jQuery event object (optional)
   */
  toggleVisibility(isVisible, stopEvent, event) {
    if (isVisible === undefined) {
      isVisible = !this.isVisible;
    }

    Logger.info('toggleVisibility', isVisible);

    const eventTypeFilters = isVisible
      ? EventTypes.STOREFINDER_SHOW_FILTERS
      : EventTypes.STOREFINDER_HIDE_FILTERS;

    const eventTypeList = isVisible
      ? EventTypes.STOREFINDER_HIDE_LIST_REQUEST
      : EventTypes.STOREFINDER_SHOW_LIST_REQUEST;
    if (this.$element) {
      this.$element.toggleClass('is-hidden', !isVisible);

      window.jq.off('resizeEnd.storeFinder-filters');
      if (isVisible) {
        window.jq.on('resizeEnd.storeFinder-filters', this.updateParentHeight.bind(this)).one(
          EventTypes.STOREFINDER_STORE_DETAILS_OPEN_REQUEST,
          function () {
            this.$element.addClass('is-hidden');
            this.$filters.trigger(EventTypes.STOREFINDER_HIDE_FILTERS, { isVisible: false });
          }.bind(this)
        );
      }

      this.isVisible = isVisible;
      this.updateParentHeight();
      this.$filters.trigger(eventTypeFilters, { isVisible });
      this.$filters.trigger(eventTypeList, { isVisible });
    }

    if (stopEvent === true && event) {
      event.preventDefault();
      event.stopPropagation();
    }
  },

  /**
   * Set href element of filters to correct value
   */
  updateLinks(url) {
    if (device.supportsPushState) {
      const $activeFilters = this.$filters.filter('.is-active');
      const activeFilters = $activeFilters.toArray().map(function (element) {
        return element.getAttribute('data-urlfriendly-label');
      });

      if (url) {
        history.pushState({ activeFilters }, document.title, url);
      }

      const baseUrl = this.baseUrl;
      const queryParams = window.location.search ? `?${window.location.search}` : '';

      this.$filters.each(function (index, element) {
        const label = element.getAttribute('data-urlfriendly-label');
        const filters = activeFilters.filter(function (val) {
          return val !== label;
        });

        if (activeFilters.indexOf(label) === -1) {
          filters.push(label);
        }

        filters.sort();
        element.href = baseUrl + (filters.length ? `/${filters.join('+')}` : '') + queryParams;
      });
    }
  },

  /**
   * Set the height of `this.$wrapper` to the height of `this.$element`
   */
  updateParentHeight() {
    const isActive = this.isVisible && !matchBreakpoint(this.settings.breakpointOverlay);
    document.body.classList.toggle('has-activeDialog', isActive);
    document.body.classList.toggle('has-activeFilterDialog', isActive);

    if (matchBreakpoint(this.settings.breakpointOverlay)) {
      this.$wrapper.css('min-height', '');
    } else {
      this.$wrapper.css('min-height', this.isVisible ? `${this.$element.outerHeight(true)}px` : '');
    }
  },
});

export default Singleton.create(StoreFinderFilters, 'StoreFinderFilters');
